import { useCallback, useState } from "react";
import {
  Card,
  Col,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSearch,
  faTags,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import Button from "components/base/Button";
import CommandPalette from "components/common/CommandPalette";
import LinkProduto from "components/common/LinkProduto";
import { useBuscarProdutosNFe } from "hooks/useNFe";
import { BuscarProdutosNFeItem } from "types/nfe";
import { TipoCalculoTabelaPreco } from "services/tabelaPrecoService";
import {
  TabelaPrecoFormValues,
  tipoCalculoLabels,
} from "pages/retaguarda/tabelaPrecoForm";

// ─── Estado da linha de adição ──────────────────────────────────────────────────

interface LinhaState {
  produto: BuscarProdutosNFeItem | null;
  tipoCalculo: TipoCalculoTabelaPreco;
  percentual: number;
}

const linhaInicial: LinhaState = {
  produto: null,
  tipoCalculo: TipoCalculoTabelaPreco.AcrescimoSobreVenda,
  percentual: 0,
};

// ─── Componente ────────────────────────────────────────────────────────────────

const RegrasEspeciaisCard = () => {
  const { control, formState: { errors } } = useFormContext<TabelaPrecoFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "regrasEspeciais",
  });

  // Estado da linha de adição
  const [linha, setLinha] = useState<LinhaState>(linhaInicial);
  const [showBusca, setShowBusca] = useState(false);
  const [termoBusca, setTermoBusca] = useState("");

  const queryBusca = useBuscarProdutosNFe(termoBusca, showBusca && termoBusca.length >= 2);

  const handleSearch = useCallback((t: string) => setTermoBusca(t), []);

  const handleSelecionarProduto = useCallback((produto: BuscarProdutosNFeItem) => {
    setLinha((prev) => ({ ...prev, produto }));
  }, []);

  const handleAdicionar = () => {
    if (!linha.produto) return;

    append({
      produtoId: linha.produto.id,
      produtoCodigo: linha.produto.codigo,
      produtoDescricao: linha.produto.descricao,
      tipoCalculo: linha.tipoCalculo,
      percentual: linha.percentual,
    });

    setLinha(linhaInicial);
  };

  const isLinhaValorFixo = linha.tipoCalculo === TipoCalculoTabelaPreco.ValorFixo;

  return (
    <>
      <Card className="mb-4">
        <Card.Header className="border-bottom">
          <h5 className="mb-0">Regras especiais (opcional)</h5>
        </Card.Header>
        <Card.Body>
          {/* Linha de adição */}
          <Row className="g-2 align-items-end mb-3">
            {/* Busca de produto */}
            <Col xs={12} md={5}>
              <Form.Group>
                <Form.Label>Produto</Form.Label>
                <div
                  role="button"
                  onClick={() => setShowBusca(true)}
                  className="form-control d-flex align-items-center justify-content-between"
                  style={{ cursor: "pointer" }}
                >
                  <span
                    className={`text-truncate ${
                      linha.produto ? "text-body-emphasis" : "text-body-tertiary"
                    }`}
                  >
                    {linha.produto
                      ? `${linha.produto.codigo} — ${linha.produto.descricao}`
                      : "Clique para buscar um produto…"}
                  </span>
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="text-body-tertiary ms-2 flex-shrink-0"
                  />
                </div>
              </Form.Group>
            </Col>

            {/* Tipo de cálculo */}
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label>Tipo de cálculo</Form.Label>
                <Form.Select
                  value={linha.tipoCalculo}
                  onChange={(e) =>
                    setLinha((prev) => ({
                      ...prev,
                      tipoCalculo: Number(e.target.value) as TipoCalculoTabelaPreco,
                    }))
                  }
                >
                  {(
                    Object.entries(tipoCalculoLabels) as [string, string][]
                  ).map(([value, label]) => (
                    <option key={value} value={Number(value)}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Valor fixo (R$) ou Percentual (%) conforme o tipo de cálculo */}
            <Col xs={12} md={2}>
              <Form.Group>
                <Form.Label>
                  {isLinhaValorFixo ? "Valor (R$)" : "Percentual (%)"}
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={linha.percentual}
                  onFocus={(e) => e.currentTarget.select()}
                  onChange={(e) =>
                    setLinha((prev) => ({
                      ...prev,
                      percentual: parseFloat(e.target.value) || 0,
                    }))
                  }
                />
              </Form.Group>
            </Col>

            {/* Botão adicionar */}
            <Col xs="auto">
              <Button
                variant="primary"
                onClick={handleAdicionar}
                disabled={!linha.produto}
                type="button"
                title="Adicionar regra"
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" />
                Adicionar
              </Button>
            </Col>
          </Row>

          {/* Tabela de regras */}
          {fields.length === 0 ? (
            <div className="text-center py-5 px-3">
              <FontAwesomeIcon
                icon={faTags}
                className="text-body-tertiary mb-3"
                style={{ fontSize: "2.5rem" }}
              />
              <h6 className="text-body-secondary">Nenhuma regra especial</h6>
              <p className="text-body-tertiary small mb-0">
                Os produtos adicionados à regra especial serão mostrados aqui.
              </p>
            </div>
          ) : (
            <Table responsive className="phoenix-table fs-9 mb-0">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>Tipo de cálculo</th>
                  <th>Percentual</th>
                  <th style={{ width: 56 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field, index) => (
                  <tr key={field.id}>
                    <td className="align-middle">
                      <LinkProduto
                        produtoId={field.produtoId}
                        className="fw-semibold text-body-emphasis"
                      >
                        {field.produtoCodigo ? `#${field.produtoCodigo} ` : ""}
                        {field.produtoDescricao ?? field.produtoId}
                      </LinkProduto>
                      {field.produtoCodigoBarras && (
                        <div className="text-body-tertiary fs-10">
                          EAN: {field.produtoCodigoBarras}
                        </div>
                      )}
                    </td>
                    <td className="align-middle text-body-secondary">
                      {tipoCalculoLabels[field.tipoCalculo]}
                    </td>
                    <td className="align-middle text-body-secondary">
                      {field.tipoCalculo === TipoCalculoTabelaPreco.ValorFixo
                        ? `R$ ${field.percentual}`
                        : `${field.percentual}%`}
                    </td>
                    <td className="align-middle">
                      <Button
                        variant="phoenix-danger"
                        size="sm"
                        className="px-2"
                        onClick={() => remove(index)}
                        type="button"
                        title="Remover regra"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* CommandPalette de busca de produto */}
      <CommandPalette
        show={showBusca}
        onHide={() => setShowBusca(false)}
        onSearch={handleSearch}
        items={queryBusca.data ?? []}
        isFetching={queryBusca.isFetching}
        isSuccess={queryBusca.isSuccess}
        onSelect={handleSelecionarProduto}
        getKey={(p) => p.id}
        placeholder="Código, descrição ou EAN..."
        emptyMessage="Nenhum produto encontrado"
        minChars={2}
        renderRow={(produto) => (
          <>
            <span className="cmd-row__doc">{produto.codigo}</span>
            <span className="cmd-row__name">{produto.descricao}</span>
            {produto.unidade && (
              <span
                style={{
                  fontSize: "0.68rem",
                  color: "var(--cmd-muted)",
                  flexShrink: 0,
                }}
              >
                {produto.unidade}
              </span>
            )}
          </>
        )}
      />
    </>
  );
};

export default RegrasEspeciaisCard;
